/** Типы ответов API (соответствуют схемам Pydantic на backend). */

export interface User {
  id: string
  email: string
  full_name: string
  is_active: boolean
  created_at: string
  last_login_at: string | null
}

export interface WorkspaceMembershipSummary {
  workspace_id: string
  name: string
  slug: string
  role: Role
  permissions: Permission[]
}

export interface MeResponse {
  user: User
  workspaces: WorkspaceMembershipSummary[]
}

export interface TokenResponse {
  access_token: string
  token_type: string
  expires_at: string
  expires_in: number
}

export interface RegisterResponse {
  user: User
  workspace: WorkspaceMembershipSummary | null
  tokens: TokenResponse
}

export interface Workspace {
  id: string
  name: string
  slug: string
  timezone: string
  currency: string
  fiscal_year_start_month: number
  week_start: number
  created_at: string
}

export interface WorkspaceWithRole extends Workspace {
  role: Role
  permissions: Permission[]
}

export interface WorkspaceListResponse {
  items: WorkspaceWithRole[]
  total: number
}

export interface Member {
  user_id: string
  membership_id: string
  email: string
  full_name: string
  role: Role
  joined_at: string
  is_active: boolean
}

export interface MemberListResponse {
  items: Member[]
  total: number
}

export interface Invitation {
  id: string
  email: string
  role: Role
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
  expires_at: string
  created_at: string
  invited_by_user_id: string | null
}

export interface InvitationListResponse {
  items: Invitation[]
  total: number
}

export interface InvitationCreatedResponse {
  invitation: Invitation
  accept_url: string
  email_sent: boolean
}

export interface MetricUsage {
  used: number
  /** null — без ограничения */
  limit: number | null
}

export interface UsageResponse {
  plan_code: string
  plan_name: string
  metrics: Record<string, MetricUsage>
}

export interface AuditLogEntry {
  id: string
  action: string
  actor_user_id: string | null
  object_type: string | null
  object_id: string | null
  ip: string | null
  request_id: string | null
  metadata: Record<string, unknown>
  created_at: string
}

export interface AuditLogListResponse {
  items: AuditLogEntry[]
  total: number
  limit: number
  offset: number
}

export type Role = 'owner' | 'admin' | 'editor' | 'viewer' | 'accountant'

/** Права, которые backend отдаёт вместе с ролью (см. permissions.py). */
export type Permission =
  | 'workspace:manage'
  | 'workspace:view'
  | 'member:manage'
  | 'member:view'
  | 'source:manage'
  | 'source:view'
  | 'dataset:mapping_edit'
  | 'dataset:rows_view'
  | 'dashboard:manage'
  | 'dashboard:view'
  | 'ai:use'
  | 'export:create'
  | 'billing:manage'
  | 'usage:view'
  | 'audit:view'

export const ROLE_TITLES: Record<Role, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  editor: 'Редактор',
  viewer: 'Наблюдатель',
  accountant: 'Бухгалтер',
}

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  owner: 'Полный доступ, управление рабочим пространством и подпиской',
  admin: 'Источники, участники, дашборды, журнал аудита',
  editor: 'Источники, дашборды, виджеты, экспорт',
  viewer: 'Только просмотр и экспорт',
  accountant: 'Как редактор, но без доступа к подписке — для внешнего бухгалтера',
}

/** Человекочитаемые названия метрик использования. */
export const USAGE_METRIC_TITLES: Record<string, string> = {
  members: 'Участники',
  sources: 'Источники данных',
  storage_mb: 'Хранилище, МБ',
  refresh_runs_per_month: 'Обновления данных за месяц',
  ai_requests_per_month: 'AI-запросы за месяц',
  ai_tokens_per_month: 'AI-токены за месяц',
  exports_per_month: 'Экспорты за месяц',
}

/** Человекочитаемые названия действий в журнале аудита. */
export const AUDIT_ACTION_TITLES: Record<string, string> = {
  'user.registered': 'Регистрация',
  'user.login': 'Вход',
  'user.login_failed': 'Неудачная попытка входа',
  'user.logout': 'Выход',
  'token.refreshed': 'Обновление сессии',
  'token.reuse_detected': 'Повторное использование сессии',
  'workspace.created': 'Создано пространство',
  'workspace.updated': 'Изменены настройки пространства',
  'workspace.deleted': 'Удалено пространство',
  'member.invited': 'Отправлено приглашение',
  'member.joined': 'Участник присоединился',
  'member.removed': 'Участник удалён',
  'member.role_changed': 'Изменена роль участника',
  'invitation.revoked': 'Приглашение отозвано',
}

// ---------- Данные (Этап 2) ----------

export interface DatasetSummary {
  id: string
  name: string
  description: string
  row_count: number
  version_count: number
  current_version_id: string | null
  /** Подтверждено ли сопоставление колонок у текущей версии. */
  mapping_confirmed: boolean
  created_at: string
  updated_at: string
}

export interface DatasetListResponse {
  items: DatasetSummary[]
  total: number
}

export type ColumnTypeName =
  'text' | 'integer' | 'decimal' | 'date' | 'datetime' | 'boolean' | 'category'

export interface ColumnProfile {
  column_index: number
  source_name: string
  normalized_name: string
  detected_type: ColumnTypeName
  total_count: number
  null_count: number
  null_ratio: number
  distinct_count: number
  unparsed_count: number
  is_candidate_key: boolean
  min_value: string | null
  max_value: string | null
  sample_values: string[]
  contains_percent: boolean
}

export type IssueSeverity = 'info' | 'warning' | 'error'

export interface QualityIssue {
  code: string
  severity: IssueSeverity
  message: string
  column_index: number | null
  affected_rows: number
  details: Record<string, unknown>
}

export type VersionStatusName = 'pending' | 'processing' | 'ready' | 'failed'
export type ProjectionStatusName = 'not_built' | 'building' | 'ready' | 'failed'

export interface DatasetVersionSummary {
  id: string
  dataset_id: string
  version_no: number
  import_mode: 'replace' | 'append'
  file_name: string
  file_size: number
  status: VersionStatusName
  projection_status: ProjectionStatusName
  row_count: number
  column_count: number
  skipped_service_rows: number
  sheet_name: string | null
  encoding: string | null
  header_row: number | null
  error_code: string | null
  error_message: string | null
  created_at: string
  completed_at: string | null
  mapping_confirmed_at: string | null
}

export interface DatasetVersionDetail extends DatasetVersionSummary {
  columns: ColumnProfile[]
  issues: QualityIssue[]
  preview: Record<string, unknown>[]
  schema_snapshot: Record<string, unknown>
}

export interface DatasetVersionListResponse {
  items: DatasetVersionSummary[]
  total: number
}

export type RunStatusName = 'queued' | 'running' | 'completed' | 'failed'

export interface RefreshRun {
  id: string
  dataset_id: string | null
  dataset_version_id: string | null
  trigger: string
  status: RunStatusName
  file_name: string | null
  error_code: string | null
  error_message: string | null
  stats: Record<string, number>
  created_at: string
  started_at: string | null
  finished_at: string | null
  duration_seconds: number | null
}

export interface RefreshRunListResponse {
  items: RefreshRun[]
  total: number
}

export interface ImportAccepted {
  dataset_id: string
  dataset_name: string
  dataset_version_id: string
  version_no: number
  refresh_run_id: string
  status: RunStatusName
  duplicate_of_version_no: number | null
  message: string
}

// ---------- Семантический слой ----------

export interface RoleInfo {
  role: string
  title: string
  description: string
  kind: 'time' | 'measure' | 'dimension' | 'identifier'
  allowed_types: string[]
  unique: boolean
  is_personal_data: boolean
}

export interface MappingItem {
  column_index: number
  source_name: string
  normalized_name: string
  column_type: ColumnTypeName
  semantic_role: string | null
  source: 'auto' | 'user' | null
  confidence: number
  reason: string | null
  allowed_roles: string[]
}

export interface WidgetAvailability {
  title: string
  description: string
  available: boolean
  missing_roles: string[]
  missing_titles: string[]
  how_to_obtain: string[]
}

export type ReadinessName = 'ready' | 'partial' | 'insufficient_data'

export interface TemplateAvailability {
  code: string
  title: string
  description: string
  readiness: ReadinessName
  missing_roles: string[]
  missing_titles: string[]
  how_to_obtain: string[]
  widgets: WidgetAvailability[]
  available_widget_count: number
}

export interface MappingState {
  dataset_version_id: string
  confirmed: boolean
  projection_status: ProjectionStatusName
  items: MappingItem[]
  templates: TemplateAvailability[]
  roles: RoleInfo[]
}

/** Человекочитаемые названия типов колонок. */
export const COLUMN_TYPE_TITLES: Record<ColumnTypeName, string> = {
  text: 'текст',
  integer: 'целое число',
  decimal: 'число',
  date: 'дата',
  datetime: 'дата и время',
  boolean: 'да/нет',
  category: 'категория',
}

/** Статусы импорта на языке пользователя. */
export const RUN_STATUS_TITLES: Record<RunStatusName, string> = {
  queued: 'В очереди',
  running: 'Обрабатывается',
  completed: 'Готово',
  failed: 'Ошибка',
}

export const READINESS_TITLES: Record<ReadinessName, string> = {
  ready: 'Можно построить',
  partial: 'Частично доступен',
  insufficient_data: 'Недостаточно данных',
}

// ---------- Дашборды и виджеты (Этап 3) ----------

export type WidgetTypeName =
  'kpi' | 'table' | 'line_chart' | 'bar_chart' | 'pie_chart' | 'ai_insight'

export type PeriodPresetName =
  | 'last_7_days'
  | 'last_30_days'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_12_months'
  | 'all_time'
  | 'custom'

export interface PeriodInfo {
  preset: PeriodPresetName
  start: string | null
  end: string | null
  title: string
}

export interface DashboardSummary {
  id: string
  name: string
  description: string
  template_code: string | null
  version: number
  widget_count: number
  created_at: string
  updated_at: string
}

export interface DashboardListResponse {
  items: DashboardSummary[]
  total: number
}

export interface WidgetPositionInfo {
  x: number
  y: number
  w: number
  h: number
}

export interface WidgetSummary {
  id: string
  dashboard_id: string
  dataset_id: string
  title: string
  type: WidgetTypeName
  position: WidgetPositionInfo
  config: Record<string, unknown>
  version: number
  origin: 'manual' | 'template' | 'ai'
  source_template_widget: string | null
  explanation: string
  created_at: string
}

export interface ResultColumnInfo {
  key: string
  title: string
  /** `time`, `dimension` или `measure` — определяет формат значения. */
  kind: string
  /** `currency`, `quantity` или `none` — единица измерения показателя. */
  unit?: string
}

export interface ChangeInfo {
  current: string
  previous: string | null
  absolute: string | null
  /** `null` — базы для процента нет: рост с нуля процентом не выражается. */
  percent: string | null
  from_zero: boolean
  direction: 'up' | 'down' | 'flat' | 'unknown'
}

export interface SeriesPointInfo {
  label: string
  value: string
  change_absolute: string | null
  change_percent: string | null
  moving_average: string | null
  z_score: string | null
  is_anomaly: boolean
  is_spike: boolean
  is_incomplete_period: boolean
}

export interface SeriesInfo {
  points: SeriesPointInfo[]
  total: string
  average: string | null
  declines: string[]
  anomalies: string[]
}

export type WidgetResultStatus = 'ok' | 'failed' | 'not_ready'

export interface WidgetResult {
  widget_id: string
  status: WidgetResultStatus
  columns: ResultColumnInfo[]
  rows: Record<string, unknown>[]
  row_count: number
  truncated: boolean
  value: string | null
  comparison: ChangeInfo | null
  series: SeriesInfo | null
  explanation: string
  period_title: string
  dataset_name: string
  dataset_version_no: number
  data_updated_at: string | null
  computed_at: string | null
  duration_ms: number
  from_cache: boolean
  notes: string[]
  error_code: string | null
  error_message: string | null
}

export interface DashboardDetail {
  dashboard: DashboardSummary
  period: PeriodInfo
  widgets: WidgetSummary[]
  results: WidgetResult[]
  currency: string
  timezone: string
}

export interface ExplainField {
  role: string
  source_name: string
}

export interface ExplainQualityIssue {
  code: string
  severity: IssueSeverity
  message: string
  affected_rows: number
}

export interface WidgetExplain {
  widget_id: string
  widget_title: string
  formula: string
  period_title: string
  dataset_name: string
  file_name: string
  dataset_version_no: number
  dataset_version_id: string
  row_count: number
  data_updated_at: string | null
  mapping_confirmed_at: string | null
  fields: ExplainField[]
  filters: string[]
  quality_issues: ExplainQualityIssue[]
  computed_at: string | null
  duration_ms: number
  plan: Record<string, unknown>
  can_view_rows: boolean
  analysis_note: string | null
}

export interface ProposalWidget {
  title: string
  description: string
  widget_type: WidgetTypeName
  available: boolean
  plan: Record<string, unknown> | null
  explanation: string
  missing_roles: string[]
  missing_titles: string[]
  how_to_obtain: string[]
  analysis: string | null
}

export interface ProposalTemplate {
  code: string
  title: string
  description: string
  available_count: number
  widgets: ProposalWidget[]
}

export interface ProposalsResponse {
  dataset_id: string
  dataset_name: string
  templates: ProposalTemplate[]
}

/** Названия типов виджетов для интерфейса. */
export const WIDGET_TYPE_TITLES: Record<WidgetTypeName, string> = {
  kpi: 'Показатель',
  table: 'Таблица',
  line_chart: 'Линия',
  bar_chart: 'Столбцы',
  pie_chart: 'Круг',
  ai_insight: 'Вывод AI',
}

// ---------- AI (Этап 4) ----------

export interface AIStatus {
  provider: string
  model: string
  /** Провайдер детерминированный — интерфейс показывает «Демо-режим AI». */
  is_demo: boolean
  max_goal_length: number
  requests_used: number
  requests_limit: number | null
  tokens_used: number
  tokens_limit: number | null
}

export interface DataGapInfo {
  requested_goal: string
  status: 'insufficient_data' | 'needs_mapping'
  missing_semantic_fields: string[]
  missing_titles: string[]
  available_semantic_fields: string[]
  how_to_obtain: string[]
  alternatives: string[]
  manual_input_allowed: boolean
  message: string
}

export type ProposalStatusName = 'pending' | 'accepted' | 'edited' | 'rejected'

export interface AIProposal {
  id: string
  ai_request_id: string
  dataset_id: string
  dashboard_id: string | null
  title: string
  description: string
  widget_type: WidgetTypeName
  plan: Record<string, unknown>
  explanation: string
  required_semantic_fields: string[]
  missing_semantic_fields: string[]
  readiness: 'ready' | 'needs_mapping' | 'insufficient_data'
  status: ProposalStatusName
  widget_id: string | null
  created_at: string
}

export interface AIProposalsResponse {
  ai_request_id: string
  provider: string
  is_demo: boolean
  dashboard_title: string
  summary: string
  proposals: AIProposal[]
  /** Приходит и вместе с предложениями: часть цели могла быть невыполнима. */
  data_gap: DataGapInfo | null
  rejected_count: number
}

export interface InsightInfo {
  id: string
  widget_id: string | null
  metric: string
  period_label: string
  actual_value: string | null
  baseline_value: string | null
  deviation_pct: string | null
  rule_code: string
  contributors: { name: string; delta: string }[]
  text: string
  confidence: 'low' | 'medium' | 'high'
  disclaimer: string
  source: 'rules' | 'ai'
  accepted_at: string | null
  created_at: string
}

export const CONFIDENCE_TITLES: Record<InsightInfo['confidence'], string> = {
  low: 'низкая уверенность',
  medium: 'средняя уверенность',
  high: 'высокая уверенность',
}
